import { ReactElement, useEffect, useState } from "react";
import { ChevronDownIcon } from "staysure-component-library";

type AccordionProps = {
  title: ReactElement | string;
  content: ReactElement;
  icon?: ReactElement;
  id?: string;
  isExpanded?: boolean;
  onClick?: () => void;
  className?: string;
  contentClassName?: string;
  buttonClassName?: string;
  isShow?: boolean;
};

function Accordion({
  title,
  content,
  icon,
  id,
  isExpanded,
  onClick,
  className = "",
  contentClassName = "",
  buttonClassName = "",
  isShow = true,
}: Readonly<AccordionProps>) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    setIsAccordionOpen(!isAccordionOpen);
  };

  useEffect(() => {
    if (isExpanded !== undefined) {
      setIsAccordionOpen(isExpanded);
    }
  }, [isExpanded]);

  return (
    <div
      className={`p-spacing-m md:p-spacing-l group ${className}`}
      id={`accordion-${id}`}
    >
      <button
        type="button"
        className={`w-full flex justify-between items-center outline-none group ${buttonClassName}`}
        onClick={handleClick}
        id={`accordion-control-${id}`}
        aria-expanded={isAccordionOpen}
        aria-controls={`${id}-panel`}
      >
        <span>{title}</span>
        {isShow && (
          <span
            className={`transition-transform duration-300 ${
              isAccordionOpen ? "rotate-180" : "rotate-0"
            } group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-interactive-focus-default`}
          >
            {icon ?? <ChevronDownIcon />}
          </span>
        )}
      </button>
      <section
        id={`${id}-panel`}
        aria-labelledby={`accordion-control-${id}`}
        className={`${
          isAccordionOpen
            ? `max-h-fit mt-spacing-l md:mt-spacing-m lg:mt-spacing-l ${contentClassName} overflow-x-visible`
            : "max-h-0 overflow-hidden"
        }`}
        hidden={!isAccordionOpen}
        data-testid={`${id}-panel-test-id`}
      >
        <div>{content}</div>
      </section>
    </div>
  );
}

export default Accordion;
